import React, { useState, memo } from 'react';
import { Copy, Check, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import TextField from './helpers/TextField';

const ShopifyOauth = ({ formData, handleChange, onConnect, loading }) => {
  const [localError, setLocalError] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [copiedTooltip, setCopiedTooltip] = useState("");
  const [connecting, setConnecting] = useState(false);

  const isConnected =
    formData.configuration.authentication.access_token &&
    formData.configuration.authentication.access_token.startsWith("shpat_") &&
    formData.configuration.authentication.authorized;

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTooltip(fieldName);
      setTimeout(() => setCopiedTooltip(""), 2000);
    });
  };

  const handleConnectClick = async () => {
    setLocalError("");
    let url = formData.configuration.authentication.shop_url || "";
    const clientId = formData.configuration.authentication.api_key;
    const clientSecret = formData.configuration.authentication.client_secret;

    url = url
      .replace("https://", "")
      .replace("http://", "")
      .replace(/\/$/, "")
      .trim();

    if (!formData.warehouse) {
      setLocalError("Debes seleccionar bodega");
      return;
    }

    if (!url || !url.includes("myshopify.com")) {
      setLocalError("La URL debe ser válida (ej: mitienda.myshopify.com)");
      return;
    }
    if (!clientId) {
      setLocalError("El Client ID es obligatorio");
      return;
    }
    if (!clientSecret) {
      setLocalError("El Client Secret es obligatorio");
      return;
    }

    setConnecting(true);
    const { success, redirectUri, error } = await onConnect(
      url,
      {
        clientId,
        clientSecret,
      },
      formData.warehouse,
    );

    if (success) {
      // Still busy until the browser leaves: a second click would start
      // another authorization.
      window.location.replace(redirectUri);
      return;
    }
    setLocalError(error);
    setConnecting(false);
  };

  const renderCopy = (text, fieldName, label) => (
    <button
      type="button"
      onClick={() => handleCopy(text, fieldName)}
      title={copiedTooltip === fieldName ? "¡Copiado!" : "Copiar"}
      aria-label={`Copiar ${label}`}
      className="p-1.5 rounded-lg text-text-secondary hover:text-ninja-green transition-colors"
    >
      {copiedTooltip === fieldName ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );

  return (
    <div data-testid="shopify-oauth" className="space-y-4 mt-2">
      <div className="bg-blue-50 dark:bg-blue-900/20 text-info p-3 rounded-xl border border-blue-200 dark:border-blue-900 text-sm">
        <strong>Integración OAuth 2.0 (Nuevo Flujo):</strong> Ingresa las
        credenciales de tu App de Shopify.
      </div>

      <TextField
        label="Shop URL"
        placeholder="mitienda.myshopify.com"
        value={formData.configuration.authentication.shop_url ?? ""}
        onChange={(e) => handleChange("shop_url", e.target.value)}
        disabled={isConnected || loading}
        error={Boolean(localError && localError.includes("URL"))}
      />

      <TextField
        label="Client ID (API Key)"
        placeholder="El ID público de tu app"
        value={formData.configuration.authentication.api_key ?? ""}
        onChange={(e) => handleChange("api_key", e.target.value)}
        disabled={isConnected || loading}
        error={Boolean(localError && localError.includes("Client ID"))}
      />

      <TextField
        label="Client Secret"
        type={showSecret ? "text" : "password"}
        placeholder="Comienza con shpss_..."
        value={formData.configuration.authentication.client_secret ?? ""}
        onChange={(e) => handleChange("client_secret", e.target.value)}
        disabled={isConnected || loading}
        error={Boolean(localError && localError.includes("Secret"))}
        endAdornment={
          <button
            type="button"
            onClick={() => setShowSecret(!showSecret)}
            aria-label={showSecret ? "Ocultar Client Secret" : "Mostrar Client Secret"}
            className="p-1.5 rounded-lg text-text-secondary hover:text-ninja-green transition-colors"
          >
            {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
      />

      {localError && (
        <div className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm">
          {localError}
        </div>
      )}

      <div className="flex justify-center mt-1">
        {isConnected ? (
          <div className="w-full py-3 rounded-full border border-ninja-green text-ninja-green text-center font-semibold">
            Conectado Exitosamente
          </div>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleConnectClick}
            disabled={loading}
            loading={connecting}
            loadingLabel="Conectando..."
          >
            Guardar y Conectar
          </Button>
        )}
      </div>

      <TextField
        label="Token Webhook"
        value={formData.configuration.authentication.token ?? ""}
        onChange={(e) => handleChange("token", e.target.value)}
      />

      <div className="h-px bg-border my-6" />

      <p className="text-sm font-bold text-ninja-dark dark:text-white mb-3">Webhooks</p>
      {formData.configuration?.create_order && formData.configuration?.update_order && formData.configuration?.cancel_order && (
        <div className="space-y-4 mb-5">
          <TextField
            label="Webhook Crear Orden"
            value={formData.configuration.create_order?.url || ""}
            disabled
            endAdornment={renderCopy(formData.configuration.create_order?.url, "create", "Webhook Crear Orden")}
          />
          <TextField
            label="Webhook Cancelar Orden"
            value={formData.configuration.cancel_order?.url || ""}
            disabled
            endAdornment={renderCopy(formData.configuration.cancel_order?.url, "cancel", "Webhook Cancelar Orden")}
          />
          <TextField
            label="Webhook Actualizar Orden"
            value={formData?.configuration?.update_order?.url || ""}
            disabled
            endAdornment={renderCopy(formData.configuration.update_order?.url, "update", "Webhook Actualizar Orden")}
          />
        </div>
      )}
    </div>
  );
};

export default memo(ShopifyOauth);
